import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'

/**
 * Invite links — `https://vera.inc/invite/<code>` made for one person or company
 * (API: apps/api/src/features/tracked-links). Platform admins only, server-side.
 *
 * TWO POPULATIONS on every row, never mixed:
 *   clicks, bot_clicks, last_click_at, top_landing_path — vera-2's own click record, real
 *     everywhere. Bots (link unfurlers, mail scanners) are counted apart.
 *   consented_visitors, pageviews, interests, top_interest — PostHog, only visitors whose
 *     browser PostHog may record, AFTER the click. Null when PostHog is not readable here;
 *     `interestSource` says why ("fixture" locally).
 */
export type InterestRow = { interest: string; count: number }
export type InterestSourceState = { status: 'ok' | 'fixture' | 'not_configured' | 'error'; message?: string }

export type TrackedLink = {
  id: string
  code: string
  url: string
  label: string
  destination: string
  person_key: string | null
  person_label: string | null
  company: { id: string; name: string } | null
  company_name: string | null
  campaign: string | null
  created_at: string
  created_by: string | null
  archived_at: string | null
  clicks: number
  bot_clicks: number
  last_click_at: string | null
  top_landing_path: string | null
  consented_visitors: number | null
  pageviews: number | null
  last_visit_at: string | null
  interests: InterestRow[] | null
  top_interest: string | null
}

export type TrackedLinksResponse = {
  links: TrackedLink[]
  interestSource: InterestSourceState
  computedAt: string
  /** VRG-712 — the range clicks and interests were cut to; null = all time (People). */
  range: { since: string; until: string; startAt?: string } | null
}

/** VRG-712 — Analytics → Website's date range (same shape as its `DayRange`). */
export type TrackedLinksRange = { since: string; until: string; from?: string }

export type TrackedLinkDetail = TrackedLink & {
  recent_clicks: Array<{ clicked_at: string; landing_path: string; referrer_host: string | null; device: string }>
  clicks_by_day: Array<{ day: string; clicks: number }>
  pages: Array<{ path: string | null; event: string; count: number; last_seen_at: string | null }> | null
  interestSource: InterestSourceState
}

export type CreateTrackedLinkPayload = {
  label: string
  destination?: string
  person_key?: string
  person_label?: string
  company_id?: string
  company_name?: string
  campaign?: string
}

export const TRACKED_LINKS_QUERY_KEY = 'internal-tracked-links'

const call = async <T,>(method: 'GET' | 'POST' | 'PATCH', url: string, data?: unknown): Promise<T> => {
  const response = await createApiCall({ method, url, data })
  if (response.status >= 400) throw new Error(response.data?.message || 'Request failed')
  return response.data as T
}

/**
 * Every link. With `range` (Analytics → Website, VRG-712) the click figures and interests count
 * only that range; without it (People) all time. Every link is listed either way.
 */
export const useTrackedLinks = (enabled = true, includeArchived = false, range?: TrackedLinksRange) =>
  useQuery<TrackedLinksResponse>({
    queryKey: [TRACKED_LINKS_QUERY_KEY, { includeArchived, range: range ?? null }],
    queryFn: () => {
      const params = new URLSearchParams()
      if (includeArchived) params.set('archived', 'include')
      if (range) {
        params.set('since', range.since)
        params.set('until', range.until)
        if (range.from) params.set('from', range.from)
      }
      const q = params.toString()
      return call<TrackedLinksResponse>('GET', `/internal/tracked-links${q ? `?${q}` : ''}`)
    },
    staleTime: 60_000,
    retry: false,
    enabled,
  })

export const useTrackedLinkDetail = (id: string | null) =>
  useQuery<TrackedLinkDetail>({
    queryKey: [TRACKED_LINKS_QUERY_KEY, 'detail', id],
    queryFn: () => call<TrackedLinkDetail>('GET', `/internal/tracked-links/${id}`),
    staleTime: 60_000,
    retry: false,
    enabled: Boolean(id),
  })

export const useTrackedLinkActions = () => {
  const queryClient = useQueryClient()
  const refresh = () => queryClient.invalidateQueries({ queryKey: [TRACKED_LINKS_QUERY_KEY] })
  return {
    create: useMutation<TrackedLink, Error, CreateTrackedLinkPayload>({
      mutationFn: (v) => call<TrackedLink>('POST', '/internal/tracked-links', v),
      onSuccess: refresh,
    }),
    // VRG-794: the label only. The code and URL never change, so a link already sent keeps working.
    relabel: useMutation<TrackedLink, Error, { id: string; label: string }>({
      mutationFn: (v) => call<TrackedLink>('PATCH', `/internal/tracked-links/${v.id}`, { label: v.label }),
      onSuccess: refresh,
    }),
    archive: useMutation<TrackedLink, Error, { id: string }>({
      mutationFn: (v) => call<TrackedLink>('POST', `/internal/tracked-links/${v.id}/archive`),
      onSuccess: refresh,
    }),
  }
}
